/**
 * IMPORT BATCH (Bloco 04, complemento) — rastreabilidade persistente do lote.
 *
 * A auditoria existente (`AuditLog`) registra o ATO, mas no app real ela é
 * descartada (`mockAudit`) e não é consultável por lote. Por isso o lote é
 * uma entidade própria e persistente: é ela que responde "de qual arquivo,
 * por quem, em que contexto e com que resultado este equipamento entrou".
 */

export type ImportSourceFormat = "XLSX" | "PDF_TEXT" | "PDF_OCR";

export type ImportBatchStatus = "PREVIEW" | "CONFIRMADO" | "FALHOU" | "REVERTIDO";

export interface ImportBatch {
  readonly id: string;
  /** Documento oficial do arquivo na Biblioteca Técnica (Bloco 03), quando anexado. */
  readonly sourceDocumentId: string | null;
  readonly sourceFilename: string;
  /** SHA-256 do arquivo: rastreabilidade e detecção de reprocessamento. */
  readonly sourceHash: string;
  readonly sourceFormat: ImportSourceFormat;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly clientId: string;
  readonly serviceOrderId: string;
  readonly projectId: string;
  readonly unitAreaId: string;
  readonly status: ImportBatchStatus;
  readonly systemsFound: number;
  readonly systemsCreated: number;
  readonly systemsReused: number;
  readonly pipelinesFound: number;
  readonly pipelinesCreated: number;
  readonly pipelinesReused: number;
  readonly corrections: number;
  readonly warnings: number;
  readonly errors: number;
  /** Duração do processamento em milissegundos. */
  readonly processingDuration: number;
  /** Ids dos Equipments criados por este lote — base da reversão compensatória. */
  readonly createdEquipmentIds: readonly string[];
  readonly failureReason: string | null;
}

export interface ImportBatchFilter {
  readonly clientId?: string;
  readonly projectId?: string;
  readonly serviceOrderId?: string;
  readonly sourceHash?: string;
  readonly status?: ImportBatchStatus;
}

export interface ImportBatchRepository {
  save(batch: ImportBatch): Promise<void>;
  findById(id: string): Promise<ImportBatch | null>;
  list(filter?: ImportBatchFilter): Promise<ImportBatch[]>;
}

/** Lotes já confirmados com o mesmo arquivo e o mesmo contexto — sinal de reprocessamento. */
export function previousRunsOf(
  batches: readonly ImportBatch[],
  sourceHash: string,
  context: { clientId: string; serviceOrderId: string; projectId: string; unitAreaId: string }
): ImportBatch[] {
  return batches.filter(batch =>
    batch.sourceHash === sourceHash &&
    batch.status === "CONFIRMADO" &&
    batch.clientId === context.clientId &&
    batch.serviceOrderId === context.serviceOrderId &&
    batch.projectId === context.projectId &&
    batch.unitAreaId === context.unitAreaId
  );
}
